import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _filter from 'lodash/filter';
import { Link } from '@zola/zola-ui';
import { toastsActions } from '@zola-helpers/client';

import {
  addExtraField,
  completeMapping,
  sendFieldMap,
  mapColumns,
  unmapColumns,
  receiveMappingErrors,
} from '../../../../actions/GuestImportActions';
import { trackUpdateGuestListStep } from '../../../../util/trackingHelper';

import MappingErrorNotification from './MappingErrorNotification';
import ListMapper from './ListMapper';
import './MatchGuestList.styles.less';

import featureFlag from '../../../../util/featureFlags';

const isSpreadsheetUploadTracking = featureFlag.get('isSpreadsheetUploadTracking');

const MatchGuestList = ({
  addField,
  busy,
  onTryAgain,
  mappingErrors,
  mappingSummaries,
  endMapping,
  sendMapping,
  setErrorMessage,
  requiredFields,
  userFields,
  zolaFields,
  extraFields,
  mapZolaColumn,
  setErrorNote,
  unmapZolaColumn,
  token,
  flowRunId,
  updateFlowRunId,
}) => {
  const makeMapObj = () => {
    const setFields = _filter(userFields, field => field.target !== null);
    const values = Object.keys(setFields).map(field => setFields[field]);
    return values.map(set => ({ from: set.name, to: set.target }));
  };

  const onUpdateGuestList = () => {
    // validity check for required fields
    // (Hardcoded, until "Full name" / "First + Last Name" relationship comes from server)
    const { 'Guest Full Name': fullName, 'Guest First Name': firstName, 'Guest Last Name': lastName } = zolaFields;
    const fullNameValid = fullName.assignment !== null;
    const firstNameValid = firstName !== undefined && firstName.assignment !== null;
    const lastNameValid = lastName !== undefined && lastName.assignment !== null;
    const isValid = fullNameValid || (firstNameValid && lastNameValid);

    return (isValid)
      ? sendMapping(token, makeMapObj(), flowRunId, updateFlowRunId)
      : setErrorMessage(['Please use "Full Name", or "First Name" & "Last Name" from Our List.']);
  };

  useEffect(() => {
    const numItems = mappingSummaries.length + mappingErrors.length;
    if (numItems > 0) {
      const time = numItems.length < 4 ? numItems.length * 2 : 10;
      setErrorNote(
        <MappingErrorNotification summaries={mappingSummaries} errors={mappingErrors} />,
        { autoDismissInSeconds: time, className: 'mapping-summary', containerClass: 'mapping' }
      );

      if(isSpreadsheetUploadTracking) trackUpdateGuestListStep('failure', flowRunId);
    }
  }, [mappingSummaries, mappingErrors]);

  return (
    <div className="guest__upload-matching">
      <h1 className="h1">Match Your List with Ours</h1>
      <p>Not seeing the correct details on your list?
        <Link
          role="button"
          className="button-upload"
          onClick={() => {
            onTryAgain();
            if(isSpreadsheetUploadTracking) trackUpdateGuestListStep('try_again', flowRunId);
          }}
        >
          Try uploading your spreadsheet again.
        </Link>
      </p>
      <ListMapper
        addExtraField={addField}
        busy={busy}
        endMapping={endMapping}
        fields={{ requiredFields, userFields, zolaFields, extraFields }}
        mapZolaColumn={mapZolaColumn}
        unmapZolaColumn={unmapZolaColumn}
        onUpdateGuestList={onUpdateGuestList}
        flowRunId={flowRunId}
      />
    </div>
  );
};

MatchGuestList.propTypes = {
  addField: PropTypes.func,
  busy: PropTypes.bool,
  onTryAgain: PropTypes.func.isRequired,
  mappingSummaries: PropTypes.arrayOf(PropTypes.string),
  mappingErrors: PropTypes.arrayOf(PropTypes.string),
  sendMapping: PropTypes.func,
  setErrorMessage: PropTypes.func,
  requiredFields: PropTypes.arrayOf(PropTypes.string),
  userFields: PropTypes.objectOf(PropTypes.shape({})),
  zolaFields: PropTypes.objectOf(PropTypes.shape({})),
  mapZolaColumn: PropTypes.func,
  setErrorNote: PropTypes.func,
  unmapZolaColumn: PropTypes.func,
  extraFields: PropTypes.arrayOf(PropTypes.string),
  flowRunId: PropTypes.string,
  updateFlowRunId: PropTypes.func,
};

const mapStateToProps = state => ({
  busy: state.guestList.guestImport.busy,
  mappingErrors: state.guestList.guestImport.mappingErrors,
  mappingSummaries: state.guestList.guestImport.mappingSummaries,
  token: state.guestList.guestImport.token,
  requiredFields: state.guestList.guestImport.requiredFields,
  userFields: state.guestList.guestImport.userFields,
  zolaFields: state.guestList.guestImport.zolaFields,
  extraFields: state.guestList.guestImport.extraFields,
});

const mapDispatchToProps = dispatch => ({
  addField: fieldName => dispatch(addExtraField(fieldName)),
  endMapping: () => dispatch(completeMapping()),
  sendMapping: (token, map, flowRunId, updateFlowRunId) => dispatch(sendFieldMap(token, map, flowRunId, updateFlowRunId)),
  setErrorMessage: msg => dispatch(receiveMappingErrors(msg, [])),
  mapZolaColumn: (userCol, zolaCol) => dispatch(mapColumns(userCol, zolaCol)),
  unmapZolaColumn: (userCol, zolaCol) => dispatch(unmapColumns(userCol, zolaCol)),
  setErrorNote: (headline, options) => dispatch(toastsActions.negative({ headline, ...options })),
});

export default connect(mapStateToProps, mapDispatchToProps)(MatchGuestList);



// WEBPACK FOOTER //
// ./src/components/manage/guestlist/upload/MatchGuestList.jsx